/*
 * ═══════════════════════════════════════════════════════════
 * CUSTOM CART DRAWER — Recharge Shadow DOM Injection v8
 * ═══════════════════════════════════════════════════════════
 *
 * ZERO-FLASH: Permanent CSS rules hide Recharge's default
 * header + empty state elements. They're never visible.
 * Our custom content is layered on top.
 *
 * Key insight from DOM capture:
 * During non-empty → empty transition, the drawer contains:
 *   - Our .cec-custom-header (from non-empty injection)
 *   - Recharge <header> with "Your cart (0)" + X button
 *   - Our .cec-progress-section (from non-empty injection)  
 *   - Recharge empty div with "Your cart is empty!" + "Continue shopping"
 * We need to ALWAYS hide Recharge's header and empty state.
 */

console.log('CART DRAWER SCRIPT EXECUTED');

// ═══════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════
const CART_CONFIG = {
  freeShippingThreshold: 0,
  milestones: [
    { label: 'Free Shipping', threshold: 0 },
    { label: 'Free Golden Spoon', threshold: 0 },
  ],
  recommendedProducts: [
    {
      handle: 'focus-flow-chai',
      title: 'Focus Flow Chai',
      description: 'Adaptogenic chai to power your day without the crash.',
      fallbackImage: 'https://cdn.shopify.com/s/files/1/0696/2246/4651/files/focus-flow-chai.jpg',
      fallbackPrice: '$28.00',
      url: '/products/focus-flow-chai',
    },
    {
      handle: 'rose-ritual-chai',
      title: 'Rose Ritual Chai',
      description: 'Rose-infused chai for calm, grounded evenings.',
      fallbackImage: 'https://cdn.shopify.com/s/files/1/0696/2246/4651/files/rose-ritual-chai.jpg',
      fallbackPrice: '$28.00',
      url: '/products/rose-ritual-chai',
    },
  ],
};

// ═══════════════════════════════════════════
// BUNDLE OFFER CONFIG
// ═══════════════════════════════════════════
// Eligible products matched by PRODUCT HANDLE (stable, rebrand-safe).
// Shopify admin already has an automatic discount for these exact products —
// this block ONLY renders the UI, it does NOT create the discount.
const BUNDLE = {
  handles: ['focus-flow-chai', 'rose-ritual-chai'],
  target: 3,
  priceLabel: '$79',
  savingsAmount: 20, // total $ off — used only as a display fallback label
  // Product used for the empty-state one-click "Buy 3 for $79" button.
  defaultHandle: 'focus-flow-chai',
};

// ═══════════════════════════════════════════
// STATE
// ═══════════════════════════════════════════
let _observer = null;
let _busy = false;
let _debounce = null;
let _progressTimer = null;
let _falseEmptyAt = 0; // timestamp of last false-empty detection — used for cooldown
let _giftProcessing = false; // set by gift IIFE during cart mutations — handle() shows overlay and skips

// ═══════════════════════════════════════════
// CSS
// ═══════════════════════════════════════════
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@400;500;600;700&display=swap');

/*
 * ─── PERMANENT HIDE: Recharge default elements ───
 * These rules stay FOREVER. They ensure:
 * 1. Recharge's default <header> (with "Your cart (0)" + X) is never visible
 * 2. Recharge's default empty state ("Your cart is empty!" + "Continue shopping") is never visible
 * Our custom .cec-custom-header replaces the header.
 * Our custom .cec-empty-body replaces the empty state.
 *
 * Recharge's header structure:
 *   <header class="border-storefront-drawer-border border-b">
 *
 * Recharge's empty state:
 *   <div class="... justify-center items-center">
 *     <div class="dynamic-richtext"><h2>Your cart is empty!</h2></div>
 *     <button>Continue shopping</button>
 *   </div>
 */
#cart-drawer > header.border-storefront-drawer-border {
  display: none !important;
}

/* Permanently hide Recharge's default empty state body — same treatment as the header */
#cart-drawer > div.justify-center.items-center {
  display: none !important;
}

/* ─── Our header ─── */
.cec-custom-header{width:100%;background:#1a3c2a;display:flex;align-items:center;justify-content:space-between;padding:20px;box-sizing:border-box;flex-shrink:0}
.cec-custom-header *{box-sizing:border-box;margin:0;padding:0}
.cec-header-back{display:flex;align-items:center;gap:8px;color:#f3e9de;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;text-decoration:none;cursor:pointer;background:none;border:none;letter-spacing:.03em;transition:opacity .2s ease}
.cec-header-back:hover{opacity:.85}
.cec-header-back svg{width:16px;height:16px;fill:none;stroke:#f3e9de;stroke-width:2.5;stroke-linecap:round;stroke-linejoin:round}
.cec-header-title{color:#f3e9de;font-family:'DM Sans',sans-serif;font-size:14px;font-weight:600;letter-spacing:.03em}

/* ─── Progress ─── */
.cec-progress-section{width:100%;text-align:center;padding:20px;box-sizing:border-box;flex-shrink:0;border-bottom:3px solid #6b7a3a}
.cec-progress-section *{box-sizing:border-box;margin:0;padding:0}
.cec-progress-label{font-family:'DM Sans',sans-serif;font-size:14px;font-weight:500;color:#1a3c2a;margin-bottom:18px;line-height:1.5}
.cec-progress-label strong{font-weight:700;color:#1a3c2a}
/* Milestones row — 3 equal columns */
.cec-milestones{display:flex;align-items:flex-start;position:relative;width:100%;padding:13px 0 8px;min-height:60px}
/* Track: starts at center of first icon, ends at center of last icon.
   Position is set inline by mkProgress() based on milestone count. */
.cec-progress-track{position:absolute;top:27px;transform:translateY(-50%);height:6px;background:#b7e4c7;border-radius:20px;z-index:1;overflow:hidden}
.cec-progress-fill{position:absolute;top:0;left:0;height:100%;width:0%;background:#2d6a4f;border-radius:20px;transition:width .4s ease}
/* Each milestone icon + label column */
.cec-milestone{display:flex;flex-direction:column;align-items:center;gap:8px;position:relative;flex:1;z-index:2}
/* Dot — locked state: muted green circle with lock icon */
.cec-milestone-dot{width:28px;height:28px;border-radius:50%;background:#b7e4c7;display:flex;align-items:center;justify-content:center;position:relative;z-index:2;box-shadow:0 0 0 3px #f6faf2;transition:background .35s ease,box-shadow .35s ease;flex-shrink:0}
.cec-milestone-dot svg{width:13px;height:13px;fill:#5a8a6a;stroke:none}
.cec-milestone-dot svg path{fill:#5a8a6a}
.cec-milestone-text{font-family:'DM Sans',sans-serif;font-size:11px;font-weight:500;color:#7a9a8a;text-align:center;line-height:1.3;white-space:nowrap}
/* Unlocked state: dark green dot with checkmark */
.cec-milestone.unlocked .cec-milestone-dot{background:#2d6a4f;box-shadow:0 0 0 3px #f6faf2}
.cec-milestone.unlocked .cec-milestone-dot svg{display:none}
.cec-milestone.unlocked .cec-milestone-dot::after{content:'\\2713';color:#fff;font-size:14px;font-weight:700;line-height:1}
.cec-milestone.unlocked .cec-milestone-text{color:#1a3c2a;font-weight:600}

/* ─── Empty body ─── */
.cec-empty-body{display:flex;flex-direction:column;align-items:center;padding:0 20px 28px;font-family:'DM Sans',sans-serif;color:#1a3c2a;background:#f6faf2;box-sizing:border-box;width:100%;flex:1;overflow-y:auto}
.cec-empty-body *{box-sizing:border-box;margin:0;padding:0}
.cec-headline-section{text-align:center;margin-bottom:28px;margin-top:24px;padding:0 8px}
.cec-headline{font-family:'Playfair Display',Georgia,serif;font-weight:400;font-style:italic;font-size:30px;line-height:1.2;color:#1a3c2a;margin-bottom:8px;letter-spacing:-.01em}
.cec-subtitle{font-family:'DM Sans',sans-serif;font-size:14px;color:#5c7a6b;font-weight:400;line-height:1.5}
.cec-products{width:100%;display:flex;flex-direction:column;gap:0;margin-bottom:24px}
.cec-product-row{display:flex;flex-direction:row;align-items:center;gap:16px;padding:16px 0;text-decoration:none;color:inherit;border-bottom:1px solid #b7e4c7;transition:opacity .2s ease}
.cec-product-row:first-child{border-top:1px solid #b7e4c7}
.cec-product-row:hover{opacity:.85}
.cec-product-img{width:90px;min-width:90px;height:90px;border-radius:8px;overflow:hidden;background:#d8f3dc;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.cec-product-img img{width:100%;height:100%;object-fit:cover;display:block}
.cec-product-info{display:flex;flex-direction:column;gap:4px;text-align:left}
.cec-product-name{font-family:'DM Sans',sans-serif;font-size:16px;font-weight:700;color:#1a3c2a;line-height:1.3}
.cec-product-desc{font-family:'DM Sans',sans-serif;font-size:13px;color:#5c7a6b;font-weight:400;line-height:1.4}
.cec-shop-all-btn{display:flex;align-items:center;justify-content:center;width:100%;padding:16px 24px;background:#1a3c2a;color:#f3e9de;font-family:'DM Sans',sans-serif;font-size:15px;font-weight:600;text-decoration:none;border:none;border-radius:10px;cursor:pointer;transition:background .25s ease,transform .15s ease;letter-spacing:.01em}
.cec-shop-all-btn:hover{background:#2d6a4f;transform:translateY(-1px)}
.cec-shop-all-btn:active{transform:translateY(0)}

/* ─── Loader ─── */
.cec-loader-screen{display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;flex:1;background:#f6faf2;gap:16px;padding:40px 20px;box-sizing:border-box}
.cec-loader-spinner{width:36px;height:36px;border:3px solid #b7e4c7;border-top-color:#2d6a4f;border-radius:50%;animation:cec-spin .7s linear infinite}
.cec-loader-text{font-family:'DM Sans',sans-serif;font-size:14px;color:#5c7a6b;font-weight:500}
@keyframes cec-spin{to{transform:rotate(360deg)}}

/* ─── Drawer overrides ─── */
#cart-drawer{background:#f6faf2 !important;position:relative}
.text-storefront-drawer-foreground{color:#04342B !important;}
.text-storefront-drawer-muted{color:#04342B !important;}
.text-storefront-drawer-accent{color:#04342B !important;}
.text-storefront-drawer-primary-foreground{color:#f3e9de !important;}

/* ─── Overlay: covers Recharge's raw content instantly while our injection is in flight ─── */
#cec-overlay{position:absolute;inset:0;z-index:99;background:#f6faf2;display:flex;flex-direction:column;box-sizing:border-box}

/* ─── Bundle offer (3 for $79) — non-empty drawer ─── */
.cec-bundle{position:relative;margin:14px 20px 0;padding:14px 16px;border:1px dashed #d64545;border-radius:8px;background:#fdecec;font-family:'DM Sans',sans-serif;color:#7a1f1f;overflow:hidden}
.cec-bundle-ribbon{position:absolute;top:10px;right:-34px;transform:rotate(35deg);background:#ffd84d;color:#3a1022;font-size:10px;font-weight:800;letter-spacing:.06em;padding:3px 36px;text-transform:uppercase;box-shadow:0 1px 3px rgba(0,0,0,.2);z-index:2}
.cec-bundle--unlocked{border-style:solid;border-color:#2d6a4f;background:#eef8f1;color:#1f4d32}
.cec-bundle-text{font-size:13px;line-height:1.45;margin-bottom:8px}
.cec-bundle-text strong{font-weight:700}
.cec-bundle-meta{display:flex;gap:6px;align-items:baseline;font-weight:600;font-size:11px;margin-bottom:6px;color:#a83232;letter-spacing:.02em}
.cec-bundle--unlocked .cec-bundle-meta{color:#1f4d32}
.cec-bundle-pct{font-weight:400;opacity:.75}
.cec-bundle-bar{width:100%;height:6px;background:#f7caca;border-radius:999px;overflow:hidden}
.cec-bundle-bar-fill{height:100%;background:#d64545;transition:width .35s ease}
.cec-bundle--unlocked .cec-bundle-bar{background:#cfe8d6}
.cec-bundle--unlocked .cec-bundle-bar-fill{background:#2d6a4f}

/* ─── Bundle CTA — empty drawer ─── */
.cec-bundle-cta{position:relative;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;width:100%;padding:18px 24px;margin-bottom:12px;background:#91014d;color:#fff;font-family:'DM Sans',sans-serif;border:none;border-radius:10px;cursor:pointer;transition:background .25s ease,transform .15s ease;letter-spacing:.01em;overflow:hidden}
.cec-bundle-cta:hover{background:#a80259;transform:translateY(-1px)}
.cec-bundle-cta:active{transform:translateY(0)}
.cec-bundle-cta[disabled]{opacity:.7;cursor:wait}
.cec-bundle-cta-title{font-size:16px;font-weight:700}
.cec-bundle-cta-sub{font-size:12px;font-weight:500;opacity:.9}
/* Corner "Save $20" ribbon — top-right of the CTA */
.cec-bundle-cta-ribbon{position:absolute;top:10px;right:-32px;transform:rotate(35deg);background:#ffd84d;color:#3a1022;font-size:10px;font-weight:800;letter-spacing:.06em;padding:3px 34px;text-transform:uppercase;box-shadow:0 1px 3px rgba(0,0,0,.2)}

/* ─── Responsive ─── */
@media(max-width:380px){
  .cec-empty-body{padding:0 14px 24px}
  .cec-headline{font-size:26px}
  .cec-product-img{width:76px;min-width:76px;height:76px}
  .cec-product-name{font-size:15px}
  .cec-milestone-text{font-size:9px}
  .cec-custom-header{padding:20px}
}
`;

// ═══════════════════════════════════════════
// SVG
// ═══════════════════════════════════════════
const LOCK = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M18 8.45H17V6.45C17 3.69 14.76 1.45 12 1.45 9.24 1.45 7 3.69 7 6.45V8.45H6C4.9 8.45 4 9.35 4 10.45V20.45C4 21.55 4.9 22.45 6 22.45H18C19.1 22.45 20 21.55 20 20.45V10.45C20 9.35 19.1 8.45 18 8.45ZM12 17.45C10.9 17.45 10 16.55 10 15.45 10 14.35 10.9 13.45 12 13.45 13.1 13.45 14 14.35 14 15.45 14 16.55 13.1 17.45 12 17.45ZM15.1 8.45H8.9V6.45C8.9 4.74 10.29 3.35 12 3.35 13.71 3.35 15.1 4.74 15.1 6.45V8.45Z"/></svg>`;
const ARROW = `<svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>`;

// ═══════════════════════════════════════════
// DATA
// ═══════════════════════════════════════════
async function fetchProduct(handle) {
  try {
    const r = await fetch(`/products/${handle}.js`);
    if (!r.ok) throw new Error(r.status);
    const p = await r.json();
    return {
      title: p.title,
      description: p.description ? p.description.replace(/<[^>]*>/g, '').substring(0, 80) + '...' : '',
      image: p.featured_image ? p.featured_image.replace(/(\.[^.]+)$/, '_180x180$1') : null,
      price: '$' + (p.price / 100).toFixed(2),
      url: `/products/${handle}`,
    };
  } catch (e) { return null; }
}

async function fetchCart() {
  try { const r = await fetch('/cart.js'); return r.ok ? await r.json() : null; }
  catch (e) { return null; }
}

// ═══════════════════════════════════════════
// BUILDERS
// ═══════════════════════════════════════════
function calcProgress(cents, itemCount) {
  const hasItems = itemCount > 0;
  const ms = CART_CONFIG.milestones;
  // All milestones unlock as soon as cart has any item, lock when empty
  const pct = hasItems ? 100 : 0;
  const unlocked = ms.map(() => hasItems);
  const label = hasItems
    ? '\uD83C\uDF89 All rewards unlocked!'
    : 'Add an item to unlock your rewards!';
  return { pct, label, unlocked };
}

function mkProgress(cents, itemCount) {
  const { pct, label, unlocked } = calcProgress(cents, itemCount);
  const n = CART_CONFIG.milestones.length;
  const trackInset = (50 / n).toFixed(3) + '%';
  return `<div class="cec-progress-section"><p class="cec-progress-label">${label}</p><div class="cec-milestones"><div class="cec-progress-track" style="left:${trackInset};right:${trackInset}"><div class="cec-progress-fill" style="width:${pct}%"></div></div>${CART_CONFIG.milestones.map((m, i) => `<div class="cec-milestone${unlocked[i] ? ' unlocked' : ''}"><div class="cec-milestone-dot">${LOCK}</div><span class="cec-milestone-text">${m.label}</span></div>`).join('')}</div></div>`;
}

function mkHeader() {
  return `<div class="cec-custom-header"><button class="cec-header-back" id="cec-back-btn">${ARROW}<span>BACK</span></button><div class="cec-header-title">CART</div></div>`;
}

function mkProductRow(p) {
  const img = p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy">` : '';
  return `<a href="${p.url}" class="cec-product-row"><div class="cec-product-img">${img}</div><div class="cec-product-info"><p class="cec-product-name">${p.title}</p><p class="cec-product-desc">${p.description}</p></div></a>`;
}

function mkFullEmptyDrawer(cents, products) {
  return `${mkHeader()}${mkProgress(cents, 0)}<div class="cec-empty-body"><div class="cec-headline-section"><h2 class="cec-headline">Your cart is empty</h2><p class="cec-subtitle">Let's find the best blend for you.</p></div><div class="cec-products" id="cec-products-container">${products.map(mkProductRow).join('')}</div>${mkBundleCTA()}<a href="/collections/all" class="cec-shop-all-btn">Shop all products</a></div>`;
}

function mkLoader() {
  return `${mkHeader()}<div class="cec-loader-screen"><div class="cec-loader-spinner"></div><div class="cec-loader-text">Loading your cart...</div></div>`;
}

// ─── Bundle offer helpers ───────────────────
// Count total quantity of eligible bundle items in the cart.
// Matches by product handle (see BUNDLE.handles). Ignores every other line item.
function countBundleItems(cart) {
  if (!cart || !cart.items) return 0;
  return cart.items.reduce(
    (n, it) => (BUNDLE.handles.indexOf(it.handle) !== -1 ? n + it.quantity : n),
    0
  );
}

// Build the progress block shown above the cart items when at least
// one eligible product is in the cart. Renders nothing otherwise.
function mkBundleOffer(count) {
  if (count <= 0) return '';
  const target = BUNDLE.target;
  const capped = count > target ? target : count;
  const pct = Math.round((capped / target) * 100);
  const remaining = target - count > 0 ? target - count : 0;
  // Matches the Shopify automatic discount "Buy Any 3 & Save $20":
  // flat $20 off once per order when 3+ eligible chais are in the cart.
  // Visualised for the 3-item / $20 unlock only (extras don't add more savings).
  //   1 eligible  → "Add 2 more eligible chais to get 3 for $79"
  //   2 eligible  → "You're 1 chai away from your 3 for $79 bundle"
  //   ≥3 eligible → "🎉 Bundle unlocked — you're getting 3 for $79 (saving $20)"
  let text;
  if (count >= target) {
    text = `<strong>🎉 Bundle unlocked — you’re getting 3 for ${BUNDLE.priceLabel} (saving $20)</strong>`;
  } else if (remaining === 1) {
    text = `You’re <strong>1 chai away</strong> from your 3 for ${BUNDLE.priceLabel} bundle`;
  } else {
    text = `Add <strong>${remaining} more eligible chais</strong> to get 3 for ${BUNDLE.priceLabel}`;
  }
  const cls = count >= target ? ' cec-bundle--unlocked' : '';
  const ribbon = count >= target ? `<span class="cec-bundle-ribbon">Saved $20</span>` : '';
  return `<div class="cec-bundle${cls}">${ribbon}<div class="cec-bundle-text">${text}</div><div class="cec-bundle-meta"><span>${capped}/${target}</span><span class="cec-bundle-pct">(${pct}%)</span></div><div class="cec-bundle-bar"><div class="cec-bundle-bar-fill" style="width:${pct}%"></div></div></div>`;
}

// Empty-state CTA: one-click "Buy 3 for $79" button.
function mkBundleCTA() {
  return `<button type="button" class="cec-bundle-cta" id="cec-bundle-cta"><span class="cec-bundle-cta-ribbon">Save 20%</span><span class="cec-bundle-cta-title">Get any 3 chais for ${BUNDLE.priceLabel}</span><span class="cec-bundle-cta-sub">Mix or match Focus Flow &amp; Rose Ritual</span></button>`;
}

// ─── Bundle line-level discount filter ──────
// Problem: Shopify's $20 bundle discount is a CART-LEVEL intent, but Recharge
// renders it per-line-item on each bundle product, showing "Saving $20" on
// every line. That's confusing and mixes badly with other discounts.
//
// Fix: when the bundle is active (≥ target eligible items), read cart.js to
// identify which discount title(s) belong to the bundle (fixed_amount allocations
// applied to bundle-handle items), then hide only those labels on bundle line
// items. Every other discount (percentage subscription, volume break, etc.)
// stays visible — we only touch the fixed-amount bundle discount.
async function filterBundleDiscounts(shadow) {
  try {
    const cart = await fetchCart();
    if (!cart || !cart.items) return;

    // Only run the filter when the bundle is actually unlocked.
    const eligibleCount = countBundleItems(cart);
    if (eligibleCount < BUNDLE.target) return;

    // Collect bundle discount titles from cart.js directly.
    // A fixed_amount allocation on a bundle-handle item IS the bundle discount.
    // Percentage discounts (e.g. 10%/15% subscription) are skipped — they stay visible.
    const bundleTitles = new Set();
    cart.items.forEach(it => {
      if (BUNDLE.handles.indexOf(it.handle) === -1) return;
      (it.line_level_discount_allocations || []).forEach(a => {
        const app = a.discount_application || {};
        if (app.value_type === 'fixed_amount' && app.title) {
          bundleTitles.add(app.title.toLowerCase());
        }
      });
    });

    // Map bundle product titles (as Recharge renders them) for DOM lookup.
    const bundleProductTitles = new Set(
      cart.items
        .filter(it => BUNDLE.handles.indexOf(it.handle) !== -1)
        .map(it => (it.product_title || '').toLowerCase())
    );

    // Generic fallback label in case Recharge renders "Saving $20" without the title.
    const fallbackLabel = `saving $${BUNDLE.savingsAmount}`;

    // Walk each line item in the drawer.
    const listItems = shadow.querySelectorAll('div[role="listitem"]');
    listItems.forEach(li => {
      const titleEl = li.querySelector('.font-semibold');
      if (!titleEl) return;
      const title = (titleEl.textContent || '').toLowerCase().trim();
      // Only act on bundle line items — leave every other line untouched.
      let isBundleLine = false;
      bundleProductTitles.forEach(t => { if (t && title.indexOf(t) !== -1) isBundleLine = true; });
      if (!isBundleLine) return;

      // Hide leaf elements whose text matches a known bundle discount title
      // OR the literal "Saving $20" fallback. Percentage subscription text
      // (e.g. "Saving 10%") is left alone — different title, different format.
      li.querySelectorAll('*').forEach(el => {
        if (el.children.length > 0) return; // leaf nodes only
        if (el.dataset && el.dataset.cecBundleHidden) return;
        const raw = (el.textContent || '').toLowerCase().trim();
        if (!raw) return;
        let shouldHide = false;
        bundleTitles.forEach(bt => { if (bt && raw.indexOf(bt) !== -1) shouldHide = true; });
        if (!shouldHide && raw.indexOf(fallbackLabel) !== -1) shouldHide = true;
        if (shouldHide) {
          el.style.display = 'none';
          if (el.dataset) el.dataset.cecBundleHidden = '1';
        }
      });
    });
  } catch (e) {
    console.warn('[bundle] filterBundleDiscounts failed', e);
  }
}

// Add BUNDLE.target units of the default eligible product to the cart.
// Shopify's automatic discount then rewrites the price at checkout.
async function addBundleToCart(shadow) {
  const btn = shadow.querySelector('#cec-bundle-cta');
  if (btn) { btn.disabled = true; btn.querySelector('.cec-bundle-cta-title').textContent = 'Adding…'; }
  try {
    const prod = await fetch(`/products/${BUNDLE.defaultHandle}.js`).then(r => r.json());
    const variantId = prod.variants && prod.variants[0] && prod.variants[0].id;
    if (!variantId) throw new Error('no variant');
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: BUNDLE.target }] }),
    });
    // Recharge re-reads the cart on next drawer open. Reloading is the most
    // reliable way to surface the new state across both the drawer and the page.
    window.location.reload();
  } catch (e) {
    console.error('[bundle] add failed', e);
    if (btn) { btn.disabled = false; btn.querySelector('.cec-bundle-cta-title').textContent = `Buy 3 for ${BUNDLE.priceLabel}`; }
  }
}

// ═══════════════════════════════════════════
// DOM
// ═══════════════════════════════════════════
function injectCSS(s) {
  if (!s.querySelector('#cec-main-css')) {
    const el = document.createElement('style');
    el.id = 'cec-main-css';
    el.textContent = CSS;
    s.appendChild(el);
  }
}

function hasOurEmpty(s) { return !!s.querySelector('.cec-empty-body'); }
// Exclude elements inside #cec-overlay — the overlay contains a loader/header clone
// that should NOT block handle() from detecting the real injection state.
function hasOurLoader(s) { const l = s.querySelector('.cec-loader-screen'); return !!l && !l.closest('#cec-overlay'); }
function hasOurHeader(s) { const h = s.querySelector('.cec-custom-header'); return !!h && !h.closest('#cec-overlay'); }
function getDrawer(s) { return s.querySelector('#cart-drawer'); }

/**
 * Detect Recharge's default empty state.
 * From DOM capture, the empty state has:
 *   <div class="... justify-center items-center">
 *     <div class="... dynamic-richtext"><h2>Your cart is empty!</h2></div>
 *     <button>Continue shopping</button>
 *   </div>
 */
function isRechargeEmpty(s) {
  const d = getDrawer(s);
  if (!d) return false;
  // Direct child div with justify-center + items-center (Recharge's empty body)
  const centered = d.querySelector(':scope > div.justify-center.items-center');
  if (centered) return true;
  // Fallback: "Continue shopping" button present + no list items
  if (s.querySelectorAll('div[role="listitem"]').length === 0) {
    for (const b of d.querySelectorAll('button')) {
      if (b.closest('.cec-custom-header')) continue;
      if (b.textContent.trim() === 'Continue shopping') return true;
    }
  }
  return false;
}

/**
 * Close drawer — click Recharge's hidden close button.
 * From DOM capture: header > div > button with lucide-x SVG
 */
function closeDrawer(s) {
  // Recharge's header is hidden but still in DOM
  const xBtn = s.querySelector('#cart-drawer > header button .lucide-x');
  if (xBtn) { xBtn.closest('button').click(); return; }
  // Fallback
  for (const b of s.querySelectorAll('button .lucide-x')) { b.closest('button').click(); return; }
}

function pause() { if (_observer) _observer.disconnect(); }
function resume(s) { if (_observer) _observer.observe(s, { childList: true, subtree: true }); }
function bindBack(s) {
  const b = s.querySelector('#cec-back-btn');
  if (b) b.addEventListener('click', (e) => { e.preventDefault(); closeDrawer(s); });
  // Bind empty-state bundle CTA (idempotent — uses a dataset flag).
  const cta = s.querySelector('#cec-bundle-cta');
  if (cta && !cta.dataset.bound) {
    cta.dataset.bound = '1';
    cta.addEventListener('click', (e) => { e.preventDefault(); addBundleToCart(s); });
  }
}

// ═══════════════════════════════════════════
// INJECTION
// ═══════════════════════════════════════════

function showLoader(shadow) {
  const d = getDrawer(shadow);
  if (!d) return;
  // Wipe everything (including stale non-empty injection) and show loader
  d.innerHTML = mkLoader();
  bindBack(shadow);
}

/**
 * Immediately cover Recharge's raw content with a branded overlay (header + spinner).
 * Called synchronously from the observer callback the moment Recharge inserts any DOM.
 * The overlay sits above everything (z-index:99, position:absolute) and is removed by
 * injectNonEmpty (or wiped by showLoader→d.innerHTML) once real content is ready.
 *
 * Guards:
 *   1. Bail if drawer already has real injected content (hasOurHeader/Loader/Empty).
 *   2. Bail if Recharge hasn't put anything in the drawer yet (nothing to cover).
 *   3. Bail if overlay already exists (idempotent).
 */
function showOverlay(shadow) {
  const d = getDrawer(shadow);
  if (!d || d.querySelector('#cec-overlay')) return;
  // Only cover if Recharge already has content in the drawer
  if (!d.querySelector(':scope > header, :scope > div.overflow-y-auto, :scope > footer, :scope > div.justify-center')) return;
  d.insertAdjacentHTML('afterbegin', `<div id="cec-overlay">${mkLoader()}</div>`);
  bindBack(shadow);
}

async function injectEmpty(shadow) {
  const d = getDrawer(shadow);
  if (!d) return;
  pause(); _busy = true;
  try {
    const cart = await fetchCart();
    const total = cart ? cart.total_price : 0;
    const products = CART_CONFIG.recommendedProducts.map(p => ({
      title: p.title, description: p.description,
      image: p.fallbackImage, price: p.fallbackPrice, url: p.url,
    }));
    d.innerHTML = mkFullEmptyDrawer(total, products);
    bindBack(shadow);
    console.log('✅ EMPTY CART READY');
    loadProducts(shadow);
  } finally { _busy = false; resume(shadow); }
}

async function loadProducts(shadow) {
  const c = shadow.querySelector('#cec-products-container');
  if (!c) return;
  const results = await Promise.all(CART_CONFIG.recommendedProducts.map(p => fetchProduct(p.handle)));
  const products = results.map((f, i) => {
    const fb = CART_CONFIG.recommendedProducts[i];
    return f ? { title: f.title, description: f.description || fb.description, image: f.image || fb.fallbackImage, price: f.price, url: f.url }
             : { title: fb.title, description: fb.description, image: fb.fallbackImage, price: fb.fallbackPrice, url: fb.url };
  });
  pause(); c.innerHTML = products.map(mkProductRow).join(''); resume(shadow);
}

async function injectNonEmpty(shadow) {
  const d = getDrawer(shadow);
  if (!d) return;
  pause(); _busy = true;
  try {
    // Remove overlay + any stale custom elements before injecting (idempotent)
    shadow.querySelector('#cec-overlay')?.remove();
    d.querySelectorAll('.cec-custom-header, .cec-progress-section').forEach(el => el.remove());
    // Insert our header at top (Recharge's header hidden via CSS)
    d.insertAdjacentHTML('afterbegin', mkHeader());
    bindBack(shadow);
    // Insert placeholder progress immediately (0% fill) so layout is stable right away.
    // Observer is paused so this won't loop.
    const body = d.querySelector(':scope > div.overflow-y-auto');
    if (body) body.insertAdjacentHTML('beforebegin', mkProgress(0, 0));
    // Fetch real total and update the already-visible bar in-place
    const cart = await fetchCart();
    const total = cart ? cart.total_price : 0;
    const count = cart ? cart.item_count : 0;
    const { pct, label: progressLabel, unlocked } = calcProgress(total, count);
    const labelEl = d.querySelector('.cec-progress-label');
    if (labelEl) labelEl.innerHTML = progressLabel;
    const fill = d.querySelector('.cec-progress-fill');
    if (fill) fill.style.width = `${pct}%`;
    d.querySelectorAll('.cec-milestone').forEach((el, i) => el.classList.toggle('unlocked', unlocked[i]));
    // Insert bundle offer right below the progress section.
    // Only renders if at least one eligible product is already in the cart.
    d.querySelectorAll('.cec-bundle').forEach(el => el.remove());
    const bundleHtml = mkBundleOffer(countBundleItems(cart));
    if (bundleHtml) {
      const progressEl = d.querySelector('.cec-progress-section');
      if (progressEl) progressEl.insertAdjacentHTML('afterend', bundleHtml);
    }
    console.log('✅ NON-EMPTY CART READY');
  } finally { _busy = false; resume(shadow); }
  // Run after resume so the hide mutation doesn't re-enter handle().
  filterBundleDiscounts(shadow);
}

async function refreshProgress(shadow) {
  const s = shadow.querySelector('.cec-progress-section');
  if (!s) return;
  const cart = await fetchCart();
  if (!cart) return;
  const { pct, label, unlocked } = calcProgress(cart.total_price, cart.item_count);
  // Pause observer so innerHTML changes don't re-trigger handle() and create a polling loop
  pause();
  const l = s.querySelector('.cec-progress-label');
  if (l) l.innerHTML = label;
  const f = s.querySelector('.cec-progress-fill');
  if (f) f.style.width = `${pct}%`;
  s.querySelectorAll('.cec-milestone').forEach((el, i) => el.classList.toggle('unlocked', unlocked[i]));
  // Re-render bundle offer alongside the progress refresh so quantity
  // changes (+/−) update the progress text/bar in real time.
  const drawer = getDrawer(shadow);
  if (drawer) {
    drawer.querySelectorAll('.cec-bundle').forEach(el => el.remove());
    const bundleHtml = mkBundleOffer(countBundleItems(cart));
    if (bundleHtml) s.insertAdjacentHTML('afterend', bundleHtml);
  }
  resume(shadow);
  // Re-filter line-level bundle "Saving $20" text after cart updates.
  filterBundleDiscounts(shadow);
  // Notify gift manager so it can re-evaluate after Recharge drawer changes
  document.dispatchEvent(new CustomEvent('cart:updated'));
}

// ═══════════════════════════════════════════
// KIT MESSAGE
// ═══════════════════════════════════════════
function addKitMsg(items) {
  items.forEach(item => {
    if (item.querySelector('.custom-kit-message')) return;
    const t = item.querySelector('.flex.items-center .w-full.text-sm.flex.flex-col.gap-2 .flex.justify-between.gap-2 .flex.flex-col .font-semibold');
    if (!t) return;
    if (t.textContent.toLowerCase().includes('kit')) {
      // TEMPORARILY DISABLED — uncomment the lines below to re-enable the kit message
      // const m = document.createElement('p');
      // m.className = 'custom-kit-message text-sm text-storefront-drawer-muted';
      // m.textContent = 'Comes free with the NEW 15 Min Clean Vegan Mylk Guide';
      // item.appendChild(m);
    }
  });
}

// ═══════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════
function handle(shadow) {
  if (_busy) return;
  const drawer = getDrawer(shadow);
  if (!drawer) return;

  // Gift mutations in progress — show overlay to cover Recharge's mid-render state
  if (_giftProcessing) {
    if (!drawer.querySelector('#cec-overlay')) {
      showOverlay(shadow);
    }
    return;
  }

  const items = shadow.querySelectorAll('div[role="listitem"]');
  const hasItems = items.length > 0;

  // Loader mid-transition → skip
  if (hasOurLoader(shadow)) return;

  // ── TRANSITION: empty→non-empty (items appeared while our empty state is showing) ──
  // Happens when drawer was showing empty state and user adds a product.
  // Recharge may partially re-render by inserting items alongside our injected elements.
  if (hasOurEmpty(shadow) && hasItems) {
    _falseEmptyAt = 0; // items appeared — clear false-empty cooldown
    console.log('[handle] → empty→non-empty: clearing stale empty, injecting non-empty');
    pause(); _busy = true;
    shadow.querySelectorAll('.cec-custom-header, .cec-progress-section, .cec-empty-body').forEach(el => el.remove());
    _busy = false; resume(shadow);
    injectNonEmpty(shadow);
    addKitMsg(items);
    return;
  }

  // Our empty state showing + no items → correct state, skip
  if (hasOurEmpty(shadow)) return;

  // ── TRANSITION: non-empty→empty (all items removed, our non-empty header still showing) ──
  // Happens when user removes the last item while drawer is open.
  // Also triggered falsely when Recharge re-renders after gift removal (0 listitems briefly).
  // MUST verify with cart API before transitioning — otherwise we show empty state while
  // the cart still has items (e.g. gift was removed but other products remain).
  if (!hasItems && hasOurHeader(shadow)) {
    if (Date.now() - _falseEmptyAt < 2000) return; // cooldown: skip re-verification
    console.log('[handle] → non-empty→empty detected → verifying with cart API...');
    _busy = true;
    fetchCart().then(cart => {
      _busy = false;
      if (cart && cart.item_count > 0) {
        // False empty: cart still has items, Recharge is mid-render
        _falseEmptyAt = Date.now();
        console.log('[handle] → False empty (non-empty→empty): cart has ' + cart.item_count + ' items, cooling down 2s');
        handle(shadow);
        return;
      }
      _falseEmptyAt = 0;
      console.log('[handle] → Confirmed empty: transitioning to empty state');
      showLoader(shadow);
      injectEmpty(shadow);
    }).catch(() => {
      _busy = false;
      _falseEmptyAt = 0;
      showLoader(shadow);
      injectEmpty(shadow);
    });
    return;
  }

  // ── EMPTY: Recharge empty state detected → VERIFY cart before injecting ──
  // Bug guard: Recharge briefly shows its own empty body during non-empty re-renders.
  // Without verification we'd inject empty layout even though the cart has items.
  //
  // Cooldown prevents a rapid-fire loop: each verification takes ~300ms (fetchCart),
  // and if isRechargeEmpty is still true when it resolves, calling handle() again
  // triggers ANOTHER 300ms verification — repeating 5–6 times = the 1–2s delay.
  // Instead: one verification, set cooldown, wait for Recharge's own mutations to
  // bring items into the DOM (which fires handle() immediately via the observer).
  if (!hasItems && isRechargeEmpty(shadow)) {
    if (Date.now() - _falseEmptyAt < 2000) return; // cooldown: skip re-verification
    console.log('[handle] → Empty detected → verifying with cart API...');
    _busy = true;
    fetchCart().then(cart => {
      _busy = false;
      if (cart && cart.item_count > 0) {
        // False empty: Recharge mid-render. Set cooldown so rapid mutations don't loop.
        _falseEmptyAt = Date.now();
        console.log('[handle] → False empty: cart has items, cooling down 2s');
        handle(shadow); // one re-evaluation — cooldown above prevents re-verification
        return;
      }
      _falseEmptyAt = 0;
      showLoader(shadow);
      injectEmpty(shadow);
    }).catch(() => {
      _busy = false;
      _falseEmptyAt = 0;
      showLoader(shadow);
      injectEmpty(shadow);
    });
    return;
  }

  // ── NON-EMPTY: no header → inject ──
  if (hasItems && !hasOurHeader(shadow)) {
    _falseEmptyAt = 0; // items rendered — clear false-empty cooldown
    console.log('[handle] → Non-empty → inject header + progress');
    injectNonEmpty(shadow);
    addKitMsg(items);
    return;
  }

  // ── NON-EMPTY: already done → updates ──
  if (hasItems && hasOurHeader(shadow)) {
    _falseEmptyAt = 0; // items present and stable — clear false-empty cooldown
    addKitMsg(items);
    clearTimeout(_progressTimer);
    _progressTimer = setTimeout(() => refreshProgress(shadow), 800);
  }
}

// ═══════════════════════════════════════════
// INIT
// ═══════════════════════════════════════════
function init() {
  const el = document.querySelector('#recharge-storefront-experiences__cart-drawer');
  if (!el || !el.shadowRoot) return;
  const shadow = el.shadowRoot;
  console.log('INITIALIZING CART CUSTOMIZATION');

  // Inject CSS immediately — this permanently hides Recharge's header
  // so it can NEVER flash, regardless of timing
  injectCSS(shadow);

  _observer = new MutationObserver(() => {
    // Synchronously cover Recharge's raw content the moment any DOM appears in the drawer.
    // showOverlay is a no-op if: already overlaid, real content already injected, or drawer
    // is empty. This gives users instant visual feedback (branded spinner) while handle()
    // does its async work (fetchCart → inject header/progress).
    const d = getDrawer(shadow);
    if (d && !_busy && !hasOurHeader(shadow) && !hasOurLoader(shadow) && !hasOurEmpty(shadow) && !d.querySelector('#cec-overlay')) {
      showOverlay(shadow);
    }
    // Fire immediately — catches single-mutation state changes (item removed, drawer opened)
    // without waiting for the debounce timer. If _busy or state is already correct, no-op.
    handle(shadow);
    // Also debounce — covers Recharge's multi-step renders where each step resets the timer.
    // Without this, rapid mutations from Recharge's render would each call handle() redundantly.
    clearTimeout(_debounce);
    _debounce = setTimeout(() => handle(shadow), 150);
  });
  _observer.observe(shadow, { childList: true, subtree: true });
  handle(shadow);
}

document.addEventListener('DOMContentLoaded', () => {
  console.log('DOM CONTENT LOADED');
  let n = 0;
  const poll = setInterval(() => {
    n++;
    const el = document.querySelector('#recharge-storefront-experiences__cart-drawer');
    if (el && el.shadowRoot) { clearInterval(poll); console.log('RECHARGE FOUND'); init(); }
    else if (n >= 50) { clearInterval(poll); console.warn('Recharge not found after 5s'); }
  }, 100);
});


/* =========================================
   CUSTOM + / − QUANTITY CONTROL
========================================= */

document.addEventListener("click", function (e) {

  // PLUS BUTTON
  if (e.target.closest(".qty-plus")) {

    const wrapper = e.target.closest(".complementary-qty");
    if (!wrapper) return;

    const input = wrapper.querySelector(".qty-input");
    if (!input) return;

    input.value = parseInt(input.value || 0) + 1;
  }

  // MINUS BUTTON
  if (e.target.closest(".qty-minus")) {

    const wrapper = e.target.closest(".complementary-qty");
    if (!wrapper) return;

    const input = wrapper.querySelector(".qty-input");
    if (!input) return;

    let current = parseInt(input.value || 1);

    if (current > 0) {
      input.value = current - 1;
    }
  }

});

/* ═══════════════════════════════════════════
   STICKY BAR → SCROLL + REAL-TIME PRICE
═══════════════════════════════════════════ */

(function () {

  // ── Parse price text like "$30.00" or "$1,234.00" → float ──
  function parsePrice(text) {
    if (!text) return 0;
    const cleaned = text.replace(/[^0-9.]/g, '');
    return parseFloat(cleaned) || 0;
  }

  // ── Get the final displayed price from a .price element ──
  // Handles both sale (.price--sale) and regular prices
  // For sale: theme renders <span>compare</span><span>final</span>
  // For regular: theme renders just one <span>price</span>
  function getPriceFromElement(priceEl) {
    if (!priceEl) return 0;
    const spans = priceEl.querySelectorAll('span');
    if (!spans.length) return 0;

    if (priceEl.classList.contains('price--sale')) {
      // Last span is the sale (final) price
      return parsePrice(spans[spans.length - 1].textContent);
    }

    // Regular price — first span
    return parsePrice(spans[0].textContent);
  }

  // ── Get main product current price ──
  // Reads from the visible price element in the product info section
  function getMainPrice() {
    const priceEl = document.querySelector(
      '.product-page-info__price .price, .one-product-info .price'
    );
    return getPriceFromElement(priceEl);
  }

  // ── Get main product quantity ──
  function getMainQty() {
    const input = document.querySelector(
      '.product-page-info__quantity input[name="quantity"]'
    );
    return input ? Math.max(1, parseInt(input.value, 10) || 1) : 1;
  }

  // ── Get total from complementary products ──
  // Reads price from existing .price spans inside each product-item
  function getComplementaryTotal() {
    let total = 0;

    document.querySelectorAll(
      '#product-recommendations product-item'
    ).forEach(function (item) {

      const qtyInput = item.querySelector('.qty-input');
      if (!qtyInput) return;

      const qty = parseInt(qtyInput.value, 10) || 0;
      if (qty <= 0) return;

      const priceEl = item.querySelector('.price');
      const price = getPriceFromElement(priceEl);

      total += price * qty;
    });

    return total;
  }

  // ── Recalculate and update sticky bar ──
  function updateStickyPrice() {
    const finalEl = document.getElementById('sticky-price-final');
    if (!finalEl) return;

    const mainPrice = getMainPrice();
    const mainQty   = getMainQty();
    const compTotal = getComplementaryTotal();

    const total = (mainPrice * mainQty) + compTotal;

    finalEl.textContent = total > 0 ? '— $' + total.toFixed(2) : '';
  }

  // ── Scroll behaviour ──
  function initScroll() {
    const btn = document.getElementById('sticky-add-to-cart');
    if (!btn) return;

    btn.addEventListener('click', function () {
      const target = document.getElementById('product-recommendations');
      if (!target) return;

      const offsetPosition = target.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    });
  }

  // ── Bind all events that should trigger a price recalc ──
  function bindEvents() {

    // Complementary +/- button clicks
    // (value is updated by your existing qty handler, we just read after)
    document.addEventListener('click', function (e) {
      if (e.target.closest('.qty-plus') || e.target.closest('.qty-minus')) {
        setTimeout(updateStickyPrice, 0);
      }

      // Main product theme quantity controls (+/- divs)
      if (e.target.closest('[data-control="+"]') || e.target.closest('[data-control="-"]')) {
        setTimeout(updateStickyPrice, 50);
      }
    });

    // Direct typing into any qty input
    document.addEventListener('input', function (e) {
      if (e.target.matches('.qty-input') ||
          e.target.matches('.product-page-info__quantity input[name="quantity"]')) {
        updateStickyPrice();
      }
    });

    // Variant change — theme updates the visible price in the DOM,
    // so we wait a tick for the DOM to update then re-read
    document.addEventListener('change', function (e) {
      if (e.target.matches('select[name="id"]')) {
        setTimeout(updateStickyPrice, 100);
      }
    });

    // Complementary products load asynchronously — re-run when they appear
    const recEl = document.querySelector('product-recommendations');
    if (recEl) {
      new MutationObserver(function () {
        setTimeout(updateStickyPrice, 100);
      }).observe(recEl, { childList: true, subtree: false });
    }
  }

  // ── Init ──
  document.addEventListener('DOMContentLoaded', function () {
    setTimeout(function () {
      initScroll();
      bindEvents();
      updateStickyPrice();
    }, 500);
  });

})();


/* =====================================================
   COMPLEMENTARY PRODUCTS — ADD WITH MAIN ATC
   Fixes:
     1. Variant ID read from data-product-variant-id (not inside <form>)
     2. Guard loosened — works regardless of wrapper class
     3. Selling plan forwarded if present on main form
     4. Single /cart/add.js call with items[]
===================================================== */

document.addEventListener("click", function (e) {

  // Only fire when the main Add-to-Cart button is clicked
  const btn = e.target.closest('[data-js-product-button-add-to-cart]');
  if (!btn) return;

  // Must be inside a product add-to-cart form
  const mainForm = btn.closest('form[data-type="add-to-cart-form"]');
  if (!mainForm) return;

  // Must NOT be a complementary product form (those are inside #product-recommendations)
  if (mainForm.closest('#product-recommendations')) return;

  console.log("🟢 Main ATC clicked — will add complementary items");

  // Delay to let the theme's own ATC handler fire first
  // (theme animates the button, handles subscription logic, opens drawer)
  setTimeout(function () {

    const items = [];

    // ─── Collect complementary products ───────────────────────────────────
    // Variant ID lives on <product-item data-product-variant-id="...">
    // NOT inside the <form> element (select is linked via form="" attribute)

    const productItems = document.querySelectorAll(
      '#product-recommendations product-item[data-product-variant-id]'
    );

    productItems.forEach(function (item) {

      const variantId = item.getAttribute('data-product-variant-id');
      if (!variantId) return;

      // Quantity from the custom qty input inside this product-item
      const qtyInput = item.querySelector('.qty-input');
      if (!qtyInput) return;

      const qty = parseInt(qtyInput.value, 10);
      if (isNaN(qty) || qty <= 0) return;

      items.push({
        id: parseInt(variantId, 10), // Shopify expects integer variant ID
        quantity: qty
      });
    });

    if (items.length === 0) {
      console.log("ℹ️ No complementary items selected (all qty = 0)");
      return;
    }

    console.log("➕ Adding complementary items:", items);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ items: items })
    })
    .then(function (res) {
      if (!res.ok) {
        return res.json().then(function (err) {
          throw new Error(err.description || 'Cart add failed');
        });
      }
      return res.json();
    })
    .then(function (data) {
      console.log("✅ Complementary items added:", data);

      // Trigger cart refresh — works with most Shopify themes + Recharge
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));

      // Also try theme-specific events if needed
      if (window.Shopify && window.Shopify.onCartUpdate) {
        window.Shopify.onCartUpdate();
      }
    })
    .catch(function (err) {
      console.error("❌ Failed to add complementary items:", err.message);
    });

  }, 900); // 900ms — enough for FastBundle + Recharge to process the main item

});


/* ═══════════════════════════════════════════
   GIFT ITEM MANAGER — DISABLED
   Previously auto-added Ritual Intention Cards when subtotal ≥ $40.
   Sale ended — feature disabled.
═══════════════════════════════════════════ */

(function () { return; // disabled — sale ended

  var GIFT_VARIANT_ID = 43929399328907;
  var GIFT_THRESHOLD = 4000; // $40.00 in cents — minimum non-gift subtotal to qualify for free gift

  // CSS injected into Recharge's shadow DOM to hide gift item controls
  var GIFT_CSS_ID = 'cec-gift-css';
  var GIFT_CSS = `
    [data-cec-gift] div.flex.items-center.rounded-storefront-drawer.border.border-storefront-drawer-border {
      display: none !important;
    }
    [data-cec-gift] button:has(.lucide-trash2) {
      display: none !important;
    }
  `;

  var _giftBusy = false;
  var _lockTimer = null;
  var _giftObserver = null;
  var _giftDebounce = null;

  // ── Helpers ──────────────────────────────────────────────

  function getShadow() {
    var el = document.querySelector('#recharge-storefront-experiences__cart-drawer');
    return (el && el.shadowRoot) ? el.shadowRoot : null;
  }

  // True only for gifts we auto-added (tagged with _gift property)
  // Manual purchases of the same variant do NOT have this tag
  function isAutoGift(item) {
    return item.variant_id === GIFT_VARIANT_ID &&
           !!(item.properties && item.properties._gift === 'true');
  }

  // Returns index (0-based) of FIRST auto-gift in cart.items, or -1
  function findGiftIndex(cart) {
    if (!cart || !cart.items) return -1;
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGift(cart.items[i])) return i;
    }
    return -1;
  }

  // Returns array of { lineIndex (1-based), item } for ALL auto-gift line items
  function getAllGiftItems(cart) {
    if (!cart || !cart.items) return [];
    var result = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (isAutoGift(cart.items[i])) {
        result.push({ lineIndex: i + 1, item: cart.items[i] });
      }
    }
    return result;
  }

  // Count non-auto-gift items (manual purchases of the gift product count as regular items)
  function nonGiftCount(cart) {
    if (!cart || !cart.items) return 0;
    var count = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isAutoGift(cart.items[i])) count++;
    }
    return count;
  }

  // Sum of (price * quantity) for all non-auto-gift items, in cents
  function nonGiftSubtotal(cart) {
    if (!cart || !cart.items) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isAutoGift(cart.items[i])) {
        total += cart.items[i].final_line_price;
      }
    }
    return total;
  }

  // Remove gift line items from highest to lowest index so earlier indices stay stable
  function removeAllGifts(giftItems) {
    var sorted = giftItems.slice().sort(function (a, b) { return b.lineIndex - a.lineIndex; });
    var p = Promise.resolve();
    sorted.forEach(function (g) {
      p = p.then(function () { return changeGiftQty(g.lineIndex, 0); });
    });
    return p;
  }

  // ── Cart API calls ────────────────────────────────────────

  function cartFetch() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: GIFT_VARIANT_ID, quantity: 1, properties: { _gift: 'true' } }]
      })
    }).then(function (r) { return r.json(); });
  }

  // lineIndex is 1-based (Shopify /cart/change.js convention)
  function changeGiftQty(lineIndex, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: qty })
    }).then(function (r) { return r.json(); });
  }

  function dispatchCartRefresh() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  // ── Core: ensure gift state matches cart state ────────────

  function ensureGift() {
    if (_giftBusy) return;
    _giftBusy = true;

    cartFetch()
      .then(function (cart) {
        var giftItems = getAllGiftItems(cart);
        var nonGift = nonGiftCount(cart);
        var subtotal = nonGiftSubtotal(cart);
        var qualifies = nonGift > 0 && subtotal >= GIFT_THRESHOLD;

        // ── Not qualified (no items, or subtotal < $40) → remove auto-added gifts ──
        if (!qualifies) {
          if (giftItems.length > 0) {
            console.log('[Gift] Removing auto-gift — non-gift subtotal $' + (subtotal / 100).toFixed(2) + ' is below $' + (GIFT_THRESHOLD / 100).toFixed(2));
            _giftProcessing = true;
            return removeAllGifts(giftItems).then(function () {
              dispatchCartRefresh();
            });
          }
          return; // no auto-gifts to remove — leave cart alone
        }

        // ── Qualified: non-gift subtotal ≥ $40 ──

        // Check for manually-added copies of the gift variant (no _gift property)
        var manualGiftItems = [];
        for (var mi = 0; mi < cart.items.length; mi++) {
          var itm = cart.items[mi];
          if (itm.variant_id === GIFT_VARIANT_ID && !isAutoGift(itm)) {
            manualGiftItems.push({ lineIndex: mi + 1, item: itm });
          }
        }

        // If manual copies exist alongside auto-gift(s), consolidate:
        // remove everything (manual + auto), then re-add one clean auto-gift
        if (manualGiftItems.length > 0 && giftItems.length > 0) {
          var allCopies = giftItems.concat(manualGiftItems);
          console.log('[Gift] Manual + auto gift detected — consolidating ' + allCopies.length + ' lines into one');
          _giftProcessing = true;
          return removeAllGifts(allCopies).then(function () {
            return addGift();
          }).then(function () {
            dispatchCartRefresh();
            scheduleLock();
          });
        }

        if (giftItems.length === 0) {
          // No gift yet → add one
          console.log('[Gift] Adding gift item — non-gift subtotal $' + (subtotal / 100).toFixed(2) + ' qualifies');
          return addGift().then(function () {
            dispatchCartRefresh();
            scheduleLock();
          });
        }

        if (giftItems.length > 1) {
          // Duplicate auto-gift line items → remove all, re-add one clean
          console.log('[Gift] Duplicate gift lines detected — consolidating');
          _giftProcessing = true;
          return removeAllGifts(giftItems).then(function () {
            return addGift();
          }).then(function () {
            dispatchCartRefresh();
            scheduleLock();
          });
        }

        // Exactly one gift line item
        var single = giftItems[0];
        if (single.item.quantity > 1) {
          // Qty was bumped manually → enforce qty=1
          console.log('[Gift] Enforcing gift qty=1');
          _giftProcessing = true;
          return changeGiftQty(single.lineIndex, 1).then(function () {
            dispatchCartRefresh();
            scheduleLock();
          });
        }

        // Gift already correct — just (re)apply lock
        scheduleLock();
      })
      .catch(function (err) { console.error('[Gift] ensureGift error:', err); })
      .finally(function () {
        var wasProcessing = _giftProcessing;
        _giftBusy = false;
        _giftProcessing = false;
        // If we were processing, Recharge may have finished re-rendering while
        // handle() was blocked. Kick handle() so it can evaluate the new state
        // (e.g. transition to empty if all items were removed).
        if (wasProcessing) {
          // Clear false-empty cooldown — gift processing changed the cart state,
          // so any prior cooldown is stale and would block handle() from
          // detecting the real empty/non-empty state.
          _falseEmptyAt = 0;
          var s = getShadow();
          if (s) setTimeout(function () { handle(s); }, 200);
        }
      });
  }

  // ── Lock gift item controls in drawer ────────────────────

  function scheduleLock() {
    clearTimeout(_lockTimer);
    _lockTimer = setTimeout(lockGiftControls, 600);
  }

  function injectGiftCSS(shadow) {
    if (shadow.querySelector('#' + GIFT_CSS_ID)) return;
    var style = document.createElement('style');
    style.id = GIFT_CSS_ID;
    style.textContent = GIFT_CSS;
    shadow.appendChild(style);
  }

  function lockGiftControls() {
    var shadow = getShadow();
    if (!shadow) return;

    cartFetch()
      .then(function (cart) {
        var giftIdx = findGiftIndex(cart);
        if (giftIdx === -1) return;

        // Get the gift product title from the cart to match against DOM text
        var giftTitle = cart.items[giftIdx].product_title || cart.items[giftIdx].title || '';

        injectGiftCSS(shadow);

        // First: clear stale locks from any previously-marked non-gift items
        shadow.querySelectorAll('div[role="listitem"][data-cec-gift]').forEach(function (el) {
          el.removeAttribute('data-cec-gift');
          delete el.dataset.cecGift;
          // Restore hidden controls
          var stepper = el.querySelector('div.flex.items-center.rounded-storefront-drawer.border.border-storefront-drawer-border');
          if (stepper) stepper.style.removeProperty('display');
          el.querySelectorAll('button').forEach(function (btn) {
            if (
              btn.querySelector('.lucide-trash2') ||
              (btn.querySelector('.sr-only') && btn.querySelector('.sr-only').textContent.toLowerCase().includes('remov'))
            ) {
              btn.style.removeProperty('display');
            }
          });
        });

        // Find the gift listitem by matching product title text, not by index
        var listitems = shadow.querySelectorAll('div[role="listitem"]');
        var giftEl = null;
        for (var li = 0; li < listitems.length; li++) {
          var titleEl = listitems[li].querySelector('.font-semibold');
          if (titleEl && giftTitle && titleEl.textContent.trim().toLowerCase().indexOf(giftTitle.toLowerCase()) !== -1) {
            giftEl = listitems[li];
            break;
          }
        }
        // Fallback: try positional index if title match fails
        if (!giftEl) giftEl = listitems[giftIdx];
        if (!giftEl) return;

        // Mark for CSS targeting
        giftEl.dataset.cecGift = 'true';
        giftEl.setAttribute('data-cec-gift', '');

        // Hide qty stepper
        var stepper = giftEl.querySelector('div.flex.items-center.rounded-storefront-drawer.border.border-storefront-drawer-border');
        if (stepper) stepper.style.setProperty('display', 'none', 'important');

        // Hide trash/remove button
        giftEl.querySelectorAll('button').forEach(function (btn) {
          if (
            btn.querySelector('.lucide-trash2') ||
            (btn.querySelector('.sr-only') && btn.querySelector('.sr-only').textContent.toLowerCase().includes('remov'))
          ) {
            btn.style.setProperty('display', 'none', 'important');
          }
        });

        console.log('[Gift] Gift item controls locked (title match: "' + giftTitle + '")');
      })
      .catch(function () {});
  }

  // Re-run lock whenever Recharge redraws the drawer
  // (listitem elements are replaced on every cart update)
  function watchDrawerForLock() {
    var shadow = getShadow();
    if (!shadow || _giftObserver) return;

    _giftObserver = new MutationObserver(function () {
      var shadow2 = getShadow();
      if (!shadow2) return;
      var listitems = shadow2.querySelectorAll('div[role="listitem"]');
      var needsLock = false;
      listitems.forEach(function (el) {
        if (el.dataset.cecGift !== 'true' && el.getAttribute('data-cec-gift') === null) {
          needsLock = true;
        }
      });
      if (needsLock) scheduleLock();

      // Debounced ensureGift on ANY drawer DOM change — catches cross-sell adds,
      // qty changes, and any other Recharge-internal cart modifications that
      // don't go through our fetch interceptor or custom event chain.
      clearTimeout(_giftDebounce);
      _giftDebounce = setTimeout(ensureGift, 1000);
    });

    _giftObserver.observe(shadow, { childList: true, subtree: true });
  }

  // ── Event hooks ──────────────────────────────────────────

  // React to any cart update events
  ['cart:refresh', 'cart:updated'].forEach(function (evt) {
    document.addEventListener(evt, function () {
      setTimeout(ensureGift, 400);
    });
  });

  // Hook into main ATC button clicks — run after complementary handler (900ms)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-js-product-button-add-to-cart]');
    if (!btn) return;
    var mainForm = btn.closest('form[data-type="add-to-cart-form"]');
    if (!mainForm || mainForm.closest('#product-recommendations')) return;
    setTimeout(ensureGift, 1200);
  });

  // ── Fetch interceptor ──────────────────────────────────
  // Catches ALL cart-modifying requests (including Recharge's own qty changes)
  // so ensureGift runs reliably when subtotal drops below $40.
  var _origFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');
    var isCartMutation = /\/cart\/(change|update|add|clear)\.js/i.test(urlStr);
    var result = _origFetch.apply(this, arguments);
    if (isCartMutation && !_giftBusy) {
      result.then(function () {
        setTimeout(ensureGift, 500);
      }).catch(function () {});
    }
    return result;
  };

  // ── Init ─────────────────────────────────────────────────

  document.addEventListener('DOMContentLoaded', function () {
    // Initial cart state check
    setTimeout(ensureGift, 800);

    // Poll for shadow root, then watch for redraws
    var n = 0;
    var poll = setInterval(function () {
      n++;
      if (getShadow()) {
        clearInterval(poll);
        watchDrawerForLock();
      } else if (n >= 50) {
        clearInterval(poll);
      }
    }, 100);
  });

})();

// ═══════════════════════════════════════════════════════════
// META PIXEL — Base Code
// ═══════════════════════════════════════════════════════════
(function () {
  if (window.fbq) {
    console.log('[FBPixel] fbq already exists — skipping library load, but still initializing our pixel');
  } else {
    console.log('[FBPixel] Initializing Meta Pixel base code...');

    var n = window.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];

    var t = document.createElement('script');
    t.async = true;
    t.src = 'https://connect.facebook.net/en_US/fbevents.js';
    var s = document.getElementsByTagName('script')[0];
    s.parentNode.insertBefore(t, s);

    // noscript fallback
    var ns = document.createElement('noscript');
    var img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = 'https://www.facebook.com/tr?id=1965404137656355&ev=PageView&noscript=1';
    ns.appendChild(img);
    document.body.appendChild(ns);
  }

  // Always init our pixel + fire PageView, even if fbq was loaded by another script
  fbq('init', '1965404137656355');
  // fbq('track', 'PageView');
  console.log('[FBPixel] PageView tracked, pixel ID: 1965404137656355');
})();

// ═══════════════════════════════════════════════════════════
// FACEBOOK PIXEL — InitiateCheckout Tracking
// (Checkout button lives inside Recharge's shadow DOM)
// ═══════════════════════════════════════════════════════════
(function () {
  function getShadowRoot() {
    var el = document.querySelector('#recharge-storefront-experiences__cart-drawer');
    return (el && el.shadowRoot) ? el.shadowRoot : null;
  }

  function trackCheckoutBtn(shadow) {
    var checkoutBtn = shadow.querySelector('button[name="checkout"]');
    if (!checkoutBtn) {
      console.log('[FBPixel] Checkout button not found in shadow DOM yet');
      return;
    }
    if (checkoutBtn.dataset.fbTracked) {
      return; // already tracked, skip silently
    }
    checkoutBtn.dataset.fbTracked = 'true';
    console.log('[FBPixel] Checkout button found and listener attached:', checkoutBtn);
    checkoutBtn.addEventListener('click', function () {
      console.log('[FBPixel] Checkout button clicked');
      if (typeof fbq === 'function') {
        fbq('track', 'InitiateCheckout');
        console.log('[FBPixel] InitiateCheckout event fired via fbq()');
      } else {
        console.error('[FBPixel] fbq is NOT available — pixel may not be loaded');
      }
    });
  }

  function initFbTracking() {
    var shadow = getShadowRoot();
    if (!shadow) {
      console.warn('[FBPixel] Shadow root not available in initFbTracking');
      return;
    }
    console.log('[FBPixel] Shadow root found — setting up checkout tracking');

    trackCheckoutBtn(shadow);

    var fbCheckoutObserver = new MutationObserver(function () {
      trackCheckoutBtn(shadow);
    });
    fbCheckoutObserver.observe(shadow, { childList: true, subtree: true });
    console.log('[FBPixel] MutationObserver attached to shadow DOM');
  }

  // Poll for shadow root availability
  document.addEventListener('DOMContentLoaded', function () {
    console.log('[FBPixel] DOMContentLoaded — polling for shadow root...');
    var n = 0;
    var poll = setInterval(function () {
      n++;
      if (getShadowRoot()) {
        clearInterval(poll);
        console.log('[FBPixel] Shadow root found after ' + n + ' polls');
        initFbTracking();
      } else if (n >= 50) {
        clearInterval(poll);
        console.warn('[FBPixel] Shadow root NOT found after 5s — checkout tracking NOT initialized');
      }
    }, 100);
  });
})();

/*
 * ═══════════════════════════════════════════════════════════
 * RC WIDGET — Subscribe & Save Background Color Injection
 * ═══════════════════════════════════════════════════════════
 *
 * Applies a custom background color to the "Subscribe & save"
 * purchase option (.rc-purchase-option__subscription[rc-selected])
 * inside Recharge's RC Widget shadow DOM.
 *
 * Why shadow DOM? The RC Widget renders inside a custom element
 * (e.g. <rc-widget>) with an encapsulated shadow root, making it
 * invisible to normal document.querySelector() and external CSS.
 *
 * Strategy:
 *   1. Scan ALL elements on the page for shadow roots.
 *   2. Inside each shadow root, look for .rc-widget__root to confirm
 *      it's the right widget.
 *   3. Target .rc-purchase-option__subscription[rc-selected] and
 *      apply the background color via inline style.
 *   4. Poll every 500ms (up to 40 attempts / 20s) to handle
 *      late-loading widgets.
 *   5. Once found, set up a MutationObserver to re-apply the color
 *      if the user switches between "One-time" and "Subscribe & save"
 *      (rc-selected attribute toggles on selection change).
 *
 * To change the color: update the hex value in applySubscriptionBackground()
 * and watchSelectionChanges() below.
 */
(function() {
  console.log('[RC Widget] Script loaded - Direct shadow root version');

  /*
 * ─── RC Widget: Override .rc-plans-button[data-checked] styles ───────
 * Direct property overrides don't work because the element uses CSS
 * variables (--rc-widget-brand-color, --rc-widget-brand-contrast-color).
 * We must override the variables themselves on .rc-widget__root.
 * Injected as a constructed stylesheet AFTER Recharge's own sheets
 * so our rules always win. Falls back to a <style> tag if needed.
 * ─────────────────────────────────────────────────────────────────────
 */
function injectRCWidgetCSS(shadowRoot) {
  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(`
      :host,
      :root,
      .rc-widget__root {
        --rc-widget-brand-contrast-color: #f3e9de !important;
      }
      .rc-plans-button[data-checked] {
        color: #f3e9de !important;
      }
    `);
    shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];
    console.log('[RC Widget] ✅ Plans button CSS injected');
  } catch(e) {
    if (!shadowRoot.querySelector('#cec-rc-css')) {
      const style = document.createElement('style');
      style.id = 'cec-rc-css';
      style.textContent = `
        :host,
        :root,
        .rc-widget__root {
          --rc-widget-brand-contrast-color: #f3e9de !important;
        }
        .rc-plans-button[data-checked] {
          color: #f3e9de !important;
        }
      `;
      shadowRoot.appendChild(style);
      console.log('[RC Widget] ✅ Plans button CSS injected via style tag');
    }
  }
}

  function applySubscriptionBackground() {
    const possibleHosts = document.querySelectorAll(
      'rc-widget, rc-storefront, [data-recharge], [id*="recharge"], [class*="recharge"]'
    );
    console.log('[RC Widget] Possible host elements:', possibleHosts.length, possibleHosts);

    const allEls = document.querySelectorAll('*');
    let shadowRoots = [];
    allEls.forEach(el => {
      if (el.shadowRoot) {
        shadowRoots.push(el);
        console.log('[RC Widget] Shadow root on:', el.tagName, el.id, el.className);
      }
    });
    console.log('[RC Widget] Total shadow roots:', shadowRoots.length);

    for (const hostEl of shadowRoots) {
      const shadow = hostEl.shadowRoot;
      const widgetRoot = shadow.querySelector('.rc-widget__root');
      console.log('[RC Widget] rc-widget__root in', hostEl.tagName, ':', widgetRoot);

      if (widgetRoot) {
        const subscriptionDiv = shadow.querySelector(
          '.rc-purchase-option__subscription[rc-selected]'
        );
        console.log('[RC Widget] Subscription div:', subscriptionDiv);

        if (subscriptionDiv) {
          subscriptionDiv.style.setProperty('background-color', '#f3e9de', 'important');
          console.log('[RC Widget] ✅ Applied! Style:', subscriptionDiv.getAttribute('style'));
          return { success: true, shadow, hostEl };
        } else {
          const allOptions = shadow.querySelectorAll('[class*="rc-purchase-option"]');
          console.log('[RC Widget] All purchase options found:', allOptions.length, allOptions);
        }
      }
    }

    console.warn('[RC Widget] ❌ Not found yet');
    return { success: false };
  }

  function watchSelectionChanges(shadowRoot) {
    console.log('[RC Widget] Watching shadow root for selection changes');
    new MutationObserver(() => {
      const oneTimeDiv = shadowRoot.querySelector(
        '.rc-purchase-option[rc-selected]'
      );
      if (oneTimeDiv) {
        oneTimeDiv.style.setProperty('background-color', '#f3e9de', 'important');
        console.log('[RC Widget] ✅ Re-applied after selection change');
      }
    }).observe(shadowRoot, {
      subtree: true,
      attributes: true,
      attributeFilter: ['rc-selected', 'rc-not-selected']
    });
  }

  function start() {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      console.log(`[RC Widget] Attempt #${attempts}`);
      const { success, shadow } = applySubscriptionBackground();

      if (success) {
        console.log('[RC Widget] ✅ Done on attempt', attempts);
        injectRCWidgetCSS(shadow);
        watchSelectionChanges(shadow);
        clearInterval(interval);
      } else if (attempts >= 40) {
        console.error('[RC Widget] ❌ Gave up after 40 attempts');
        clearInterval(interval);
      }
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();


// hide offer popup on mobile 

  if (window.innerWidth <= 768) {
    const observer = new MutationObserver(() => {
      const banner = document.querySelector('.kl-teaser-VZqtKm');
      if (banner) {
        banner.style.display = 'none';
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }


const observer = new MutationObserver(() => {
  const overlay = document.querySelector('.bg-black\\/20');

  if (overlay) {
    overlay.style.setProperty('background-color', 'rgba(0,0,0,0.7)', 'important');
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});